/* eslint-disable */
import React from "react";
import { TreeView, TreeItem } from "@material-ui/lab";
import { useHierarchicalSelector } from "./useHierarchicalSelector";
import Input from "./Input";
import Search from "./Search";
import Popover from "./Popover";
import AddBoxOutlinedIcon from "../../../../../../icons/OpenTree";
import RemoveOutlinedIcon from "../../../../../../icons/CloseTree";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import { hierarchies, Hierarchy } from "../../../../../bulkexport";
import { MessageDescriptor, useIntl } from "react-intl";

const useParentStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    selected: {
      "& $content": {
        background: "none !important",
      },
      "& $label": {
        background: "none !important",
      },
    },
    content: {
      borderRadius: "4px",
      padding: "6px",
      height: "32px",
    },
    label: {
      color: "rgba(39, 58, 57, 0.8)",
      fontSize: "12px",
      lineHeight: "100%",
      fontWeight: "bold",
      background: "none !important",
    },
  })
);

const useItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginBottom: "2px",
      "& $content:hover": {
        background: "rgba(51, 51, 51, 0.05)",
      },
    },
    content: {
      borderRadius: "4px",
      padding: "6px",
      height: "32px",
    },
    label: {
      color: "rgba(39, 58, 57, 0.8)",
      fontSize: "12px",
      lineHeight: "100%",
      fontWeight: "bold",
      background: "none !important",
    },
    selected: {
      "& $content": {
        background: "rgba(101, 172, 30, 0.2)",
      },
      "& $label": {
        background: "none !important",
        color: "#65AC1E",
      },
    },
  })
);

interface SelectControlProps {
  label: MessageDescriptor;
  value?: number;
  items: { id: number; name: string | MessageDescriptor }[];
  onChange: (id: number) => void;
  addSearch?: boolean;
}
export const SelectControl: React.FC<SelectControlProps> = (props) => {
  const { value, items, onChange, label, addSearch = false } = props;

  const { formatMessage } = useIntl();
  const { search, handleSearch } = useHierarchicalSelector();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const classesItem = useItemStyles();
  const classesParent = useParentStyles();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const nodes = React.useMemo(
    () =>
      hierarchies<any>(
        items
          .map((x) => ({ ...x, id: String(x.id), name: typeof x.name === "string" ? x.name : formatMessage(x.name), parent: undefined }))
          .filter((x) => !search || x.name.trim().toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())),
        "id",
        "parent"
      ),
    [items, search, formatMessage]
  );
  const open = Boolean(anchorEl);
  const togglePopover = (e) => (open ? handleClose() : handleClick(e));

  const handleToggle = (e, value) => {
    for (const id of value) {
      onChange(Number(id));
      handleClose();
    }
  };

  const customNode = (hierarchy: Hierarchy<any>) => {
    if (hierarchy.children.length) {
      return (
        <TreeItem role="listitem" key={hierarchy.item.name} nodeId={hierarchy.id} label={hierarchy.item.name} classes={classesParent}>
          {hierarchy.children.map(customNode)}
        </TreeItem>
      );
    }
    return <TreeItem role="listitem" key={hierarchy.item.name} nodeId={hierarchy.id} label={hierarchy.item.name} classes={classesItem} />;
  };

  const inputValue = (items && items.find((x) => x.id === value)?.name) || "";

  return (
    <>
      <Input
        label={formatMessage(label)}
        value={typeof inputValue === "string" ? inputValue : formatMessage(inputValue)}
        open={open}
        onClick={togglePopover}
      />
      <Popover anchorEl={anchorEl} onClose={togglePopover}>
        {addSearch && <Search onChange={handleSearch} />}
        {
          <TreeView
            defaultCollapseIcon={<RemoveOutlinedIcon />}
            defaultExpandIcon={<AddBoxOutlinedIcon />}
            role="list"
            defaultExpanded={nodes.map((x) => x.id)}
            selected={[String(value)]}
            multiSelect
            onNodeSelect={handleToggle}
          >
            {!nodes?.length && "" ? "No results" : null}
            {nodes.map(customNode)}
          </TreeView>
        }
      </Popover>
    </>
  );
};
